import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { Table } from '@widgets/table';
import { formatCurrency, usePagination } from '@shared/lib';
import { PageTitle } from '@shared/ui';
import type { Clinic } from '@shared/model';

import { AddClinicButton, AdminClinicsPagePagination, ArchiveClinicButton } from './index';
import { fetchClinics } from '@app/utils/clinic';
import { ClinicsTableView } from '../models';
import { styled } from '@mui/material';

const columnHelper = createColumnHelper<Clinic>();

export function AdminClinicsPage() {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [view, setView] = useState(ClinicsTableView.ACTIVE);
  const [rowSelection, setRowSelection] = useState({});

  const paginationProps = usePagination();

  const history = useHistory();

  const fetch = useCallback(() => {
    fetchClinics(
      view === ClinicsTableView.ARCHIVE,
      paginationProps.limit,
      paginationProps.page * paginationProps.limit
    ).then(({ clinics, count }) => {
      setClinics(clinics);
      paginationProps.setTotal(count);
    });
  }, [view, paginationProps.limit, paginationProps.page]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    setRowSelection({});
  }, [view]);

  const columns = useMemo<ColumnDef<Clinic, string>[]>(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => {
          return (
            <Checkbox
              size='small'
              checked={table.getIsAllPageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          );
        },
        //table.getToggleAllPageRowsSelectedHandler()
        cell: ({ row }) => {
          return (
            <Checkbox
              size='small'
              checked={row.getIsSelected()}
              onClick={(e) => e.stopPropagation()}
              onChange={row.getToggleSelectedHandler()}
            />
          );
        },
      }),
      columnHelper.accessor('name', {
        id: 'clinic-name',
        header: 'Название',
      }),
      columnHelper.accessor('city', {
        id: 'clinic-city',
        header: 'Город',
      }),
      columnHelper.accessor('address', {
        id: 'clinic-address',
        header: 'Адрес',
      }),
      columnHelper.accessor((clinic) => formatCurrency(clinic.smsPrice), {
        id: 'clinic-price',
        header: 'Стоимость отзыва',
      }),
      columnHelper.accessor((clinic) => `${clinic.smsSentCount} / ${clinic.smsContractCount}`, {
        id: 'clinic-sms',
        header: 'СМС отправлено/договор',
      }),
    ],
    []
  );

  const selectedClinicIds = useMemo(() => {
    return Object.entries(rowSelection).reduce((result, [id, selected]) => {
      if (selected) {
        result.push(id);
      }
      return result;
    }, [] as string[]);
  }, [rowSelection]);

  const addClinic = (clinic: Clinic) => setClinics([clinic, ...clinics]);

  const toggleView = (view: ClinicsTableView) => setView(view);

  const onRowClick = (row: Row<Clinic>) => {
    history.push(`/admin/clinics/${row.original.id}`);
  };

  return (
    <PageWrapper direction='column' spacing={2} className='content'>
      <PageTitle
        title='Клиники'
        addButton={
          <ToggleButtonGroup
            color='primary'
            value={view}
            exclusive
            size='small'
            onChange={(_, view) => toggleView(view)}
            aria-label='Archive'
            sx={{
              '&.MuiToggleButtonGroup-root': { alignItems: 'center' },
              '& .MuiToggleButton-root': {
                padding: '0 8px',
              },
            }}
          >
            <ToggleButton value={ClinicsTableView.ACTIVE} color='success'>
              Активные
            </ToggleButton>
            <ToggleButton value={ClinicsTableView.ARCHIVE} color='error'>
              Архивные
            </ToggleButton>
          </ToggleButtonGroup>
        }
        action={
          <Stack direction='row' alignItems='center' spacing={1}>
            <ArchiveClinicButton ids={selectedClinicIds} view={view} fetch={fetch} />
            <AddClinicButton onSuccess={addClinic} />
          </Stack>
        }
      />
      <Table<Clinic>
        columns={columns}
        data={clinics}
        onRowClick={onRowClick}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        getRowId={(row) => row.id.toString()}
      />

      <AdminClinicsPagePagination {...paginationProps} />
    </PageWrapper>
  );
}

const PageWrapper = styled(Stack)`
  height: 100%;
  display: grid;
  grid-template-rows: max-content 1fr max-content;
`;
